<script setup lang="ts">
import { apiUser } from '@api/user';
// import IconGoogle from '@assets/icons/google.svg?component';
import BaseCheck from '@components/quiz_v2/BaseCheck/BaseCheck.vue';
import QuizNavigation from '@components/quiz_v2/QuizNavigation/QuizNavigation.vue';
import { passwordRules } from '@helpers/validators';
import { MARKETING_CONSENT_LABEL, UserRegisterPayload } from '@models/user';
import { useAnalyticsStore, useQuizStore, useUserStore } from '@stores/index';
import { useVuelidate } from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';
import { watchDebounced } from '@vueuse/core';
import emailSpellChecker from '@zootools/email-spell-checker';
import { ref, reactive, onMounted, computed } from 'vue';

type Props = {
  quizHandle?: string
}

const props = withDefaults(defineProps<Props>(), {
  quizHandle: '',
});

const emit = defineEmits<{ success: [], back: [] }>();
const userStore = useUserStore();
const inputRef = ref<HTMLElement | null>(null);
const inputPasswordRef = ref<HTMLElement | null>(null);
const isEmailRisky = ref(false);

const siteHandle = window.siteHandle;

const formData = reactive<UserRegisterPayload>({
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  quizHandle: props.quizHandle,
  marketingConsent: true,
});

const rules = {
  email: { email, required },
  password: passwordRules,
};

const quizStore = useQuizStore();
const analyticsStore = useAnalyticsStore();

const v$ = useVuelidate(rules, formData);

const suggestedEmail = computed(() => emailSpellChecker.run({
  email: formData.email,
}));

const setSuggestedEmail = (email: string) => {
  formData.email = email;
};

const onSubmit = async () => {
  formData.name = quizStore.fullName;
  formData.firstName = quizStore.firstname;
  formData.lastName = quizStore.lastname;
  formData.siteHandle = siteHandle;
  formData.consent = {
    consentEmailPersonalizedQuiz: formData.marketingConsent ?? false,
  };

  const isValid = await v$.value.$validate();

  if (!isValid) {
    return;
  }

  const result = await userStore.register(formData);

  if (result) {
    // Send submission, we need dob field right after registration
    await quizStore.forceAnswer();
    // Refresh profile store and re-init analytics with new data
    await userStore.refreshUserProfile();

    emit('success');
  }
};

// const authWithGoogle = () => {
//   const loginUrl = new URL('/actions/social-login/auth/login', window.location.origin);
//   loginUrl.searchParams.set('provider', 'google');
//
//   const returnUrl = `${window.location.href.split('?')[0]}?oauth=google`;
//   loginUrl.searchParams.set('redirect', returnUrl);
//
//   window.location.href = loginUrl.toString();
// };

onMounted(async () => {
  inputRef.value?.focus();

  const params = new URLSearchParams(window.location.search);
  const isGoogleOAuth = params.get('oauth') === 'google';

  if (isGoogleOAuth) {
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    await userStore.getInfo();
    await userStore.refreshUserProfile();
    await quizStore.forceAnswer();
    emit('success');
  }
});

watchDebounced(formData, async () => {
  isEmailRisky.value = false;

  if (!formData.email || !formData.email.includes('@')) {
    return;
  }

  try {
    const res = await apiUser.verifyEmail(formData.email);

    if (['undeliverable', 'do_not_send'].includes(res.data.result)) {
      isEmailRisky.value = true;
    }
  } catch (e) {
    // Ignore errors
  }
},
{ debounce: 2000, maxWait: 4000 });
</script>

<template>
  <div class="quiz-signup sm:f-col-4 f-col-full">
    <form class="flex flex-col gap-16">
      <p>
        Your password must: contain at least one uppercase letter (A-Z), one lowercase letter (a-z), one numeric digit (0-9), have no spaces.
      </p>

      <input
        ref="inputRef"
        v-model.trim="formData.email"
        class="input"
        :class="{ 'input--error': v$.email.$error }"
        type="text"
        name="email"
        autocomplete="off"
        placeholder="Email Address"
        @keydown.enter="inputPasswordRef?.focus()"
      >
      <p
        v-if="isEmailRisky"
        class="text-orange"
      >
        There's a high chance this email is not real. Please double check.
      </p>
      <p
        v-if="suggestedEmail"
        class="text-orange"
      >
        Did you mean <a
          href="#"
          class="underline"
          @click.prevent="setSuggestedEmail(suggestedEmail.full)"
        >{{ suggestedEmail.full }}</a>?
      </p>
      <p
        v-for="error in v$.email.$errors"
        :key="error.$uid"
        class="text-red"
      >
        {{ error.$message }}
      </p>

      <input
        ref="inputPasswordRef"
        v-model="formData.password"
        class="input"
        :class="{ 'input--error': v$.password.$error }"
        type="password"
        name="password"
        autocomplete="off"
        placeholder="Create Password"
        @keydown.enter="onSubmit"
      >
      <p
        v-for="error in v$.password.$errors"
        :key="error.$uid"
        class="text-red"
      >
        {{ error.$message }}
      </p>

      <BaseCheck
        v-model="formData.marketingConsent"
        class="quiz-signup__marketing-consent gap-10 text-12"
        type="checkbox"
      >
        <span>{{ MARKETING_CONSENT_LABEL }}</span>
      </BaseCheck>

      <QuizNavigation
        class="relative sm:self-center"
        next-class="!min-w-[254px]"
        back-class=""
        next-text="Create my account"
        :next-loading="userStore.loading"
        :next-disabled="v$.$error"
        @next="onSubmit"
        @back="emit('back')"
      />

      <!--      <p class="text-center">-->
      <!--        or-->
      <!--      </p>-->

      <!--      <BaseButton-->
      <!--        class="w-full bg-white text-black"-->
      <!--        theme="outlined"-->
      <!--        type="button"-->
      <!--        data-testpl="login-google-button"-->
      <!--        @click="authWithGoogle"-->
      <!--      >-->
      <!--        <span class="flex items-center gap-8">-->
      <!--          <IconGoogle class="size-20" />-->
      <!--          Continue with Google-->
      <!--        </span>-->
      <!--      </BaseButton>-->

      <p class="text-16 sm:text-center">
        Already a member?
        <button
          class="font-bold hover:text-primary-500"
          type="button"
          @click="$root.goToSignInWithReturn()"
        >
          Sign in
        </button>
      </p>
    </form>

    <div class="quiz-signup__text text mt-48 text-14 sm:text-center">
      <p>We take your privacy very seriously.</p>
      <p>All your data is 256 bit SSL/TLS encrypted.</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.quiz-signup__text {
  p {
    margin-top: 0;
  }
}

.quiz-signup__marketing-consent {
  align-items: flex-start;
  line-height: 1.35;
}
</style>
